import { fromBase64 } from "../../../../../utils/base64";
import { getFormattedDate } from "../../../../../utils/dateFormatt";
import {
  TConfigItemType,
  TVerificationAppFieldConfigItem,
} from "../../../../app/models";
import { TDocumentData } from "../../../models/document";
import {
  formatBooleanConfigValue,
  formatCountryDisplayValue,
  formatSelectDisplayValue,
  formatSourceOfActiveDisplayLines,
  formatUrlDisplayValue,
  resolveConfigFieldLabel,
} from "../../../utils/configFieldDisplay";
import { getFilesForField } from "../../../utils/fileFieldMatching";
import { parseTaxEntries } from "../../../utils/parseTaxEntries";
import { getParsedTaxId } from "../../../utils/getParsedTaxId";
import { PdfRow, PdfAttachment } from "./types";

type NamedFieldConfig = TVerificationAppFieldConfigItem & { name: string };

export type FieldResult = {
  rows: PdfRow[];
  attachments: PdfAttachment[];
};

function serializePlain(
  field: TVerificationAppFieldConfigItem,
  value: unknown
): FieldResult {
  if (value == null || value === "") {
    return { rows: [], attachments: [] };
  }
  return {
    rows: [{ label: resolveConfigFieldLabel(field), value: String(value) }],
    attachments: [],
  };
}

function serializeBoolean(
  field: TVerificationAppFieldConfigItem,
  value: unknown
): FieldResult {
  const formattedValue = formatBooleanConfigValue(value);
  if (formattedValue === "") {
    return { rows: [], attachments: [] };
  }
  return {
    rows: [{ label: resolveConfigFieldLabel(field), value: formattedValue }],
    attachments: [],
  };
}

function serializeDate(
  field: TVerificationAppFieldConfigItem,
  value: unknown
): FieldResult {
  if (value == null || value === "") {
    return { rows: [], attachments: [] };
  }
  try {
    const date = typeof value === "string" ? new Date(value) : (value as Date);
    if (isNaN(date.getTime())) return { rows: [], attachments: [] };
    return {
      rows: [
        { label: resolveConfigFieldLabel(field), value: getFormattedDate(date) },
      ],
      attachments: [],
    };
  } catch {
    return { rows: [], attachments: [] };
  }
}

function serializeTaxId(
  field: TVerificationAppFieldConfigItem,
  value: unknown,
  countriesRegex: any
): FieldResult {
  const entries = parseTaxEntries(value, "");
  const hasEntries = entries.some((e) => e.country || e.value);
  if (!hasEntries) return { rows: [], attachments: [] };

  const rows: PdfRow[] = entries.map((entry) => {
    const parsed = getParsedTaxId(entry, countriesRegex);
    const label = `${parsed.viewCountry} ${resolveConfigFieldLabel(field)}`;
    return { label, value: parsed.value ?? "" };
  });
  return { rows, attachments: [] };
}

function serializeFilePicker(
  field: NamedFieldConfig,
  fileItems: TDocumentData[]
): FieldResult {
  const files = getFilesForField(field.name, fileItems);
  const attachments: PdfAttachment[] = files.map((file) => ({
    label: file.fileInfo?.kind || file.dataId,
    file,
  }));
  return { rows: [], attachments };
}

type ReportCheck = {
  name: string;
  failed: boolean;
  review_needed: boolean;
  description: string;
};

type ReportParsed = {
  title?: string;
  checks?: ReportCheck[];
};

/**
 * Serializes IdentificationReport or ScreeningReport binary data into PDF rows (title + check lines).
 */
export function serializeReport(data: TDocumentData): PdfRow[] {
  let parsed: ReportParsed;
  try {
    parsed = JSON.parse(fromBase64(data.dataBase64));
  } catch {
    return [];
  }
  const title = parsed.title || data.dataId;
  const rows: PdfRow[] = [{ label: title, value: "" }];
  for (const check of parsed.checks ?? []) {
    const status = check.failed
      ? "Failed"
      : check.review_needed
        ? "Review needed"
        : "Passed";
    rows.push({
      label: check.name,
      value: `${status} - ${check.description}`,
    });
  }
  return rows;
}

/**
 * Serializes a single config field into PDF rows and/or attachments based on field type.
 */
export function serializeField(
  field: TVerificationAppFieldConfigItem,
  data: Record<string, unknown>,
  fileItems: TDocumentData[],
  countriesRegex: any
): FieldResult {
  if (field.type === TConfigItemType.NOTE) {
    return { rows: [], attachments: [] };
  }

  if (typeof field.name !== "string") {
    return { rows: [], attachments: [] };
  }

  const value = data[field.name];

  switch (field.type) {
    case TConfigItemType.TEXT:
      return serializePlain(field, value);
    case TConfigItemType.URL:
      return serializePlain(field, formatUrlDisplayValue(value));
    case TConfigItemType.SOURCE_OF_ACTIVE:
      return serializePlain(
        field,
        formatSourceOfActiveDisplayLines(value).join("\n")
      );
    case TConfigItemType.SELECT:
      return serializePlain(field, formatSelectDisplayValue(field, value));
    case TConfigItemType.COUNTRY_SELECT:
      return serializePlain(field, formatCountryDisplayValue(value));
    case TConfigItemType.CHECKBOX:
    case TConfigItemType.PEP:
      return serializeBoolean(field, value);
    case TConfigItemType.DATE:
      return serializeDate(field, value);
    case TConfigItemType.TAX_ID:
      return serializeTaxId(field, value, countriesRegex);
    case TConfigItemType.FILE_PICKER:
      return serializeFilePicker(field as NamedFieldConfig, fileItems);
    default:
      return serializePlain(field, value);
  }
}
